/** Parse comma-separated or legacy singular URL params into unique ID list. */
export function parseProcFilterIds(
  params: URLSearchParams,
  pluralKey: string,
  singularKey: string,
): string[] {
  const fromList =
    params
      .get(pluralKey)
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean) ?? [];
  const single = params.get(singularKey)?.trim();
  if (single && !fromList.includes(single)) fromList.push(single);
  return [...new Set(fromList)];
}

export function setProcFilterIds(
  params: URLSearchParams,
  pluralKey: string,
  singularKey: string,
  ids: string[],
) {
  params.delete(singularKey);
  if (ids.length) params.set(pluralKey, ids.join(','));
  else params.delete(pluralKey);
}

export function countProcEntityFilters(
  customerIds: string[],
  vendorIds: string[],
  productIds: string[],
): number {
  return (
    (customerIds.length > 0 ? 1 : 0) +
    (vendorIds.length > 0 ? 1 : 0) +
    (productIds.length > 0 ? 1 : 0)
  );
}

export function hasProcEntityFilters(
  customerIds: string[],
  vendorIds: string[],
  productIds: string[],
): boolean {
  return countProcEntityFilters(customerIds, vendorIds, productIds) > 0;
}

export function readProcEntityFiltersFromHash(): {
  customerIds: string[];
  vendorIds: string[];
  productIds: string[];
} {
  if (typeof window === 'undefined') {
    return { customerIds: [], vendorIds: [], productIds: [] };
  }
  const hash = window.location.hash;
  const query = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : '';
  const params = new URLSearchParams(query);
  return {
    customerIds: parseProcFilterIds(params, 'customerIds', 'customerId'),
    vendorIds: parseProcFilterIds(params, 'vendorIds', 'vendorId'),
    productIds: parseProcFilterIds(params, 'productIds', 'productId'),
  };
}

export interface ProcEntityFilterIds {
  customerIds: string[];
  vendorIds: string[];
  productIds: string[];
}

export function readProcEntityFiltersFromParams(
  params: URLSearchParams,
): ProcEntityFilterIds {
  return {
    customerIds: parseProcFilterIds(params, 'customerIds', 'customerId'),
    vendorIds: parseProcFilterIds(params, 'vendorIds', 'vendorId'),
    productIds: parseProcFilterIds(params, 'productIds', 'productId'),
  };
}

/** Client-side safety net when server filters are unavailable or stale. */
export function filterProcRowsByEntities<
  T extends {
    customerId?: string | null;
    vendorId?: string | null;
    products?: Array<{ productId?: string | null; product?: { id?: string | null } | null }> | null;
  },
>(rows: T[], filters: ProcEntityFilterIds): T[] {
  if (!hasProcEntityFilters(filters.customerIds, filters.vendorIds, filters.productIds)) {
    return rows;
  }

  const customerSet = new Set(filters.customerIds.map(String));
  const vendorSet = new Set(filters.vendorIds.map(String));
  const productSet = new Set(filters.productIds.map(String));

  return rows.filter((row) => {
    if (customerSet.size > 0) {
      const id = row.customerId != null ? String(row.customerId) : '';
      if (!id || !customerSet.has(id)) return false;
    }
    if (vendorSet.size > 0) {
      const id = row.vendorId != null ? String(row.vendorId) : '';
      if (!id || !vendorSet.has(id)) return false;
    }
    if (productSet.size > 0) {
      const lineProductIds = (row.products ?? []).map((line) =>
        String(line.product?.id ?? line.productId ?? ''),
      );
      if (!lineProductIds.some((id) => id && productSet.has(id))) return false;
    }
    return true;
  });
}
